import { ScrollArea } from "@arcadeai/design-system/components/ui/atoms/scroll-area";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { DeploymentStatus } from "@/app/servers/types";
import { LogsContent } from "./logs-content";
import { LogsToolbar } from "./logs-toolbar";
import {
  type LogEntry,
  type TimeRangeValue,
  useLogsSource,
} from "./use-logs-source";

type LogLevel = "info" | "error" | "warning";

// Statuses for which an open SSE stream makes sense. Any status outside this
// set (current: "failed"; future terminal states) can't stream live logs, so
// the viewer drops the realtime option for it.
const STREAMING_STATUSES = new Set<DeploymentStatus>([
  "pending",
  "updating",
  "unknown",
  "running",
]);

// A deployment can stream live logs only while it is active. An undefined
// status (not yet loaded) is treated as streamable so we don't disrupt the
// default realtime view before the status resolves.
const canStreamLogs = (status?: DeploymentStatus): boolean =>
  !status || STREAMING_STATUSES.has(status);

const TIME_RANGE_OPTIONS: Array<{ label: string; value: TimeRangeValue }> = [
  { label: "Real-time", value: "realtime" },
  { label: "Last 15 minutes", value: "15m" },
  { label: "Last hour", value: "1h" },
  { label: "Last 6 hours", value: "6h" },
  { label: "Last 24 hours", value: "24h" },
  { label: "Last 7 days", value: "7d" },
];

// Range shown when realtime streaming isn't available (terminal deployment).
const TERMINAL_DEFAULT_RANGE: TimeRangeValue = "1h";

const TIME_RANGE_CONFIG: Record<
  Exclude<TimeRangeValue, "realtime">,
  { unit: "minutes" | "hours" | "days"; amount: number }
> = {
  "15m": { unit: "minutes", amount: 15 },
  "1h": { unit: "hours", amount: 1 },
  "6h": { unit: "hours", amount: 6 },
  "24h": { unit: "hours", amount: 24 },
  "7d": { unit: "days", amount: 7 },
};

const BOTTOM_TOLERANCE_PX = 5;

// Check if scroll viewport is at the bottom
const isAtBottom = (viewport: HTMLElement): boolean => {
  const { scrollTop, clientHeight, scrollHeight } = viewport;
  return (
    Math.abs(scrollHeight - (scrollTop + clientHeight)) <= BOTTOM_TOLERANCE_PX
  );
};

// Scroll to bottom when new logs arrive in real-time mode
const scrollToBottom = (viewport: HTMLElement) => {
  viewport.scrollTop = viewport.scrollHeight;
};

const getTimeRange = (value: TimeRangeValue) => {
  if (value === "realtime") {
    return { startTimeUtc: undefined, endTimeUtc: undefined };
  }

  const endTime = new Date();
  const startTime = new Date(endTime);

  const rangeConfig = TIME_RANGE_CONFIG[value] ?? TIME_RANGE_CONFIG["1h"];

  if (rangeConfig.unit === "minutes") {
    startTime.setMinutes(endTime.getMinutes() - rangeConfig.amount);
  } else if (rangeConfig.unit === "hours") {
    startTime.setHours(endTime.getHours() - rangeConfig.amount);
  } else {
    startTime.setDate(endTime.getDate() - rangeConfig.amount);
  }

  return {
    startTimeUtc: startTime.toISOString(),
    endTimeUtc: endTime.toISOString(),
  };
};

type LogViewerProps = {
  serverId: string;
  isManaged?: boolean;
  deploymentStatus?: DeploymentStatus;
};

const filterLogs = (
  logs: LogEntry[],
  searchQuery: string,
  selectedLevels: LogLevel[]
): LogEntry[] => {
  if (!logs) {
    return [];
  }

  const hasSearch = Boolean(searchQuery.trim());
  const hasLevelFilters = selectedLevels.length > 0;

  if (!(hasSearch || hasLevelFilters)) {
    return logs;
  }

  return logs.filter((log) => {
    const matchesSearch = hasSearch
      ? log.data.toLowerCase().includes(searchQuery.toLowerCase())
      : true;
    // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
    const logLevel = log.type as LogLevel;
    const matchesLevel = hasLevelFilters
      ? selectedLevels.includes(logLevel)
      : true;
    return matchesSearch && matchesLevel;
  });
};

export function LogsViewer({
  serverId,
  isManaged = false,
  deploymentStatus,
}: LogViewerProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLevels, setSelectedLevels] = useState<LogLevel[]>([]);
  const [timeRange, setTimeRange] = useState<TimeRangeValue>("realtime");

  // Terminal deployments (e.g. failed) can't stream live logs. Drop the
  // realtime option and fall back to a bounded historical window so the time
  // selector reflects what is actually being shown instead of a frozen "live".
  const streamingAllowed = canStreamLogs(deploymentStatus);
  const timeRangeOptions = useMemo(
    () =>
      streamingAllowed
        ? TIME_RANGE_OPTIONS
        : TIME_RANGE_OPTIONS.filter((option) => option.value !== "realtime"),
    [streamingAllowed]
  );

  // `timeRange` holds the user's selection; `effectiveTimeRange` is what we
  // actually show. When streaming isn't available we render a "realtime"
  // selection as a bounded window instead. Deriving this (rather than mutating
  // `timeRange`) means the selection and the fetched window never disagree on a
  // transition frame, and resuming a deployment restores realtime automatically.
  const effectiveTimeRange: TimeRangeValue =
    streamingAllowed || timeRange !== "realtime"
      ? timeRange
      : TERMINAL_DEFAULT_RANGE;

  const { startTimeUtc, endTimeUtc } = useMemo(
    () => getTimeRange(effectiveTimeRange),
    [effectiveTimeRange]
  );

  const { logs, error, isLoading } = useLogsSource({
    serverId,
    isManaged,
    timeRange: effectiveTimeRange,
    startTimeUtc,
    endTimeUtc,
  });

  const filteredLogs = useMemo(
    () => filterLogs(logs, searchQuery, selectedLevels),
    [logs, searchQuery, selectedLevels]
  );

  const scrollAreaRef = useRef<HTMLDivElement | null>(null);
  const prevLogsLengthRef = useRef<number>(0);
  const wasAtBottomRef = useRef<boolean>(true);
  const scrollableElementRef = useRef<HTMLElement | null>(null);
  const scrollHandlerRef = useRef<(() => void) | null>(null);
  const lastScrolledTimeRangeRef = useRef<TimeRangeValue | null>(null);

  // Clean up scroll listener
  const cleanupScrollListener = useCallback(() => {
    if (scrollHandlerRef.current && scrollableElementRef.current) {
      scrollableElementRef.current.removeEventListener(
        "scroll",
        scrollHandlerRef.current
      );
    }
  }, []);

  // Set up scroll listener for real-time mode
  const setupScrollListener = useCallback(
    (element: HTMLElement) => {
      if (effectiveTimeRange !== "realtime") {
        return;
      }

      lastScrolledTimeRangeRef.current = null; // Reset when switching to real-time

      const handleScroll = () => {
        if (scrollableElementRef.current) {
          wasAtBottomRef.current = isAtBottom(scrollableElementRef.current);
        }
      };

      scrollHandlerRef.current = handleScroll;
      element.addEventListener("scroll", handleScroll);
      handleScroll(); // Initial check
    },
    [effectiveTimeRange]
  );

  // Update scrollable element ref and scroll listener when ScrollArea changes
  useLayoutEffect(() => {
    cleanupScrollListener();

    if (!scrollAreaRef.current) {
      scrollableElementRef.current = null;
      scrollHandlerRef.current = null;
      return;
    }

    const viewport = scrollAreaRef.current.querySelector(
      "[data-slot=scroll-area-viewport]"
    );
    if (!viewport) {
      scrollableElementRef.current = null;
      scrollHandlerRef.current = null;
      return;
    }

    // The scrollable element is usually the first child of the viewport
    // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
    const scrollableElement = viewport.firstElementChild as HTMLElement | null;
    scrollableElementRef.current =
      scrollableElement &&
      scrollableElement.scrollHeight > scrollableElement.clientHeight
        ? scrollableElement
        : // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
          (viewport as HTMLElement);

    if (scrollableElementRef.current) {
      setupScrollListener(scrollableElementRef.current);
    }
  }, [cleanupScrollListener, setupScrollListener]);

  // Clean up scroll listener on unmount
  useLayoutEffect(
    () => () => {
      if (scrollHandlerRef.current && scrollableElementRef.current) {
        scrollableElementRef.current.removeEventListener(
          "scroll",
          scrollHandlerRef.current
        );
      }
    },
    []
  );

  // Scroll to bottom when historical logs are first loaded
  useLayoutEffect(() => {
    const scrollableElement = scrollableElementRef.current;
    if (!scrollableElement || effectiveTimeRange === "realtime") {
      return;
    }

    // Only scroll if we haven't already scrolled for this time range and we have logs
    if (
      filteredLogs.length > 0 &&
      lastScrolledTimeRangeRef.current !== effectiveTimeRange
    ) {
      scrollToBottom(scrollableElement);
      wasAtBottomRef.current = true; // Mark as at bottom since we just scrolled there
      lastScrolledTimeRangeRef.current = effectiveTimeRange;
    }
  }, [effectiveTimeRange, filteredLogs.length]); // Trigger when time range changes or logs load

  // Auto-scroll to bottom when new logs arrive in real-time mode (only if user was at bottom)
  useLayoutEffect(() => {
    const scrollableElement = scrollableElementRef.current;
    if (!scrollableElement || effectiveTimeRange !== "realtime") {
      return;
    }

    const hasNewLogs = filteredLogs.length > prevLogsLengthRef.current;

    if (hasNewLogs && wasAtBottomRef.current) {
      scrollToBottom(scrollableElement);
    }

    prevLogsLengthRef.current = filteredLogs.length;
  }, [filteredLogs.length, effectiveTimeRange]);

  const handleLevelToggle = (value: LogLevel) => {
    setSelectedLevels((prev) =>
      prev.includes(value)
        ? prev.filter((level) => level !== value)
        : [...prev, value]
    );
  };

  const handleTimeRangeChange = (value: TimeRangeValue) => {
    setTimeRange(value);
  };

  return (
    <div className="w-full space-y-3">
      <LogsToolbar
        isManaged={isManaged}
        onLevelToggle={handleLevelToggle}
        onResetFilters={() => setSelectedLevels([])}
        onSearchChange={setSearchQuery}
        onTimeRangeChange={handleTimeRangeChange}
        searchQuery={searchQuery}
        selectedLevels={selectedLevels}
        timeRange={effectiveTimeRange}
        timeRangeOptions={timeRangeOptions}
      />

      <div className="rounded-lg border bg-background">
        <ScrollArea className="h-[600px]" ref={scrollAreaRef}>
          <LogsContent
            error={error}
            isLoading={isLoading}
            logs={filteredLogs}
          />
        </ScrollArea>
      </div>
    </div>
  );
}
